import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GiKnifeFork } from 'react-icons/gi';
import { FcGoogle } from 'react-icons/fc';
import { ref, get, update } from 'firebase/database';
import { db } from '../firebase/firebase';

function Login() {
  const { loginWithEmail, registerWithEmail, loginWithGoogle } = useAuth();
  const [mode,       setMode]       = useState('register'); // default to register
  const [email,      setEmail]      = useState('');
  const [password,   setPassword]   = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Email / Password submit ────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await loginWithEmail(email, password);
      } else {
        const code = inviteCode.trim();
        if (!code) {
          setError('An invite code is required to create an account.');
          setLoading(false);
          return;
        }

        const codeRef  = ref(db, `inviteCodes/${code}`);
        const codeSnap = await get(codeRef);

        if (!codeSnap.exists()) {
          setError('Invalid invite code. Please check and try again.');
          setLoading(false);
          return;
        }

        if (!codeSnap.val().active) {
          setError('This invite code has already been used.');
          setLoading(false);
          return;
        }

        await registerWithEmail(email, password);
        await update(codeRef, { active: false, usedAt: new Date().toISOString() });
      }
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  // ── Google sign-in ─────────────────────────────────────────────────────────
  // Attempt Google sign-in first, then check if the user is new.
  // New user → validate and consume invite code.
  // Existing user → let them straight through.
  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await loginWithGoogle();

      const isNewUser = result?.additionalUserInfo?.isNewUser
        ?? (result?.user?.metadata?.creationTime === result?.user?.metadata?.lastSignInTime);

      if (isNewUser) {
        const code = inviteCode.trim();
        if (!code) {
          await result.user.delete();
          setError('An invite code is required to create an account. Please enter one above and try again.');
          setLoading(false);
          return;
        }

        const codeRef  = ref(db, `inviteCodes/${code}`);
        const codeSnap = await get(codeRef);

        if (!codeSnap.exists() || !codeSnap.val().active) {
          await result.user.delete();
          setError(!codeSnap.exists()
            ? 'Invalid invite code. Please check and try again.'
            : 'This invite code has already been used.'
          );
          setLoading(false);
          return;
        }

        await update(codeRef, { active: false, usedAt: new Date().toISOString() });
      }
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Google sign-in was cancelled.');
      } else if (err.code) {
        setError(friendlyError(err.code));
      } else {
        setError('Google sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError('');
    setInviteCode('');
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#fafafa',
    }}>
      <div style={{
        background: 'white', borderRadius: '16px',
        padding: '2.5rem 2rem', width: '100%', maxWidth: '400px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2rem', justifyContent: 'center' }}>
          <GiKnifeFork style={{ fontSize: '1.8rem', color: '#494949' }} />
          <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#313131' }}>Delicious Recipes</span>
        </div>

        <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.1rem', color: '#313131', textAlign: 'center' }}>
          {mode === 'login' ? 'Sign in to your account' : 'Create an account'}
        </h2>

        {error && (
          <div style={{
            background: '#fff0f0', border: '1px solid #ffcdd2',
            borderRadius: '8px', padding: '0.75rem 1rem',
            color: '#c62828', fontSize: '0.88rem', marginBottom: '1rem',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#494949' }}>Email</label>
            <input
              type="email" required
              value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{ padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.95rem' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#494949' }}>Password</label>
            <input
              type="password" required
              value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              style={{ padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.95rem' }}
            />
          </div>

          {/* Invite code — register mode only */}
          {mode === 'register' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#494949' }}>
                Invite Code <span style={{ color: '#c62828' }}>*</span>
              </label>
              <input
                type="text" required
                value={inviteCode} onChange={e => setInviteCode(e.target.value)}
                placeholder="Enter your invite code"
                style={{
                  padding: '0.6rem 0.8rem', borderRadius: '8px',
                  border: '1px solid #ddd', fontSize: '0.95rem',
                  letterSpacing: '0.05em',
                }}
              />
              <span style={{ fontSize: '0.75rem', color: '#aaa' }}>
                Required for both email and Google sign-up.
              </span>
            </div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              padding: '0.7rem', borderRadius: '8px', border: 'none',
              background: '#494949', color: 'white', fontSize: '0.95rem',
              fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Please wait…' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '1.25rem 0' }}>
          <div style={{ flex: 1, height: '1px', background: '#eee' }} />
          <span style={{ color: '#aaa', fontSize: '0.8rem' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: '#eee' }} />
        </div>

        <button
          onClick={handleGoogle}
          disabled={loading}
          style={{
            width: '100%', padding: '0.7rem', borderRadius: '8px',
            border: '1px solid #ddd', background: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '10px', fontSize: '0.95rem', fontWeight: 600,
            color: '#313131', cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}
        >
          <FcGoogle style={{ fontSize: '1.2rem' }} />
          Continue with Google
        </button>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.88rem', color: '#888' }}>
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button
            onClick={switchMode}
            style={{ background: 'none', border: 'none', color: '#E67E22', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}
          >
            {mode === 'login' ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  );
}

function friendlyError(code) {
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return "Invalid email or password. If you don't have an account, please sign up.";
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/popup-closed-by-user':
      return 'Google sign-in was cancelled.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export default Login;
